import { FC, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Tooltip from '@splunk/react-ui/Tooltip';
import alertTypeService from '../../legacy/app/alerts/alertTypeService';

type MutingStates = {
    isMuted?: boolean;
    triggeredWhileMuted?: boolean;
    triggeredNotificationSent?: boolean;
};

const PROPERTY_OR_EXPIRED_MUTING_SUFFIX =
    'Muting was applied by a property-based muting rule or the muting rule on the detector has expired.';
const DETECTOR_MUTING_SUFFIX = 'Click to view muting rules for the detector.';

const MutedChip: FC<{
    eventState: string;
    mutingStates: MutingStates | undefined;
    detectorId: string;
    hasDetectorMutingRule: boolean;
}> = ({ eventState, mutingStates, detectorId, hasDetectorMutingRule }) => {
    const [tooltipText, setTooltipText] = useState('');
    const [label, setLabel] = useState('');

    const isMuted = mutingStates?.isMuted;
    const triggeredWhileMuted = mutingStates?.triggeredWhileMuted;
    const triggeredNotificationSent = mutingStates?.triggeredNotificationSent;

    useEffect(() => {
        const suffix = hasDetectorMutingRule
            ? DETECTOR_MUTING_SUFFIX
            : PROPERTY_OR_EXPIRED_MUTING_SUFFIX;

        if (isMuted) {
            const eventStateText = alertTypeService.getDisplayFor(eventState).toLowerCase();
            setTooltipText(
                `This alert was ${eventStateText} while a muting rule was active. No notifications were sent. ${suffix}`
            );
            setLabel('notifications muted');
        } else if (triggeredWhileMuted && triggeredNotificationSent) {
            setTooltipText('Alert was muted but the muting period expired and an alert was sent.');
            setLabel('muted then sent');
        } else {
            setTooltipText('');
            setLabel('');
        }
    }, [
        eventState,
        isMuted,
        triggeredWhileMuted,
        triggeredNotificationSent,
        hasDetectorMutingRule,
    ]);

    const mutingHref = useMemo(
        () => (hasDetectorMutingRule ? `#/muting?detectorId=${detectorId}` : undefined),
        [hasDetectorMutingRule, detectorId]
    );

    const content = (
        <div className={`label-badge muted${hasDetectorMutingRule ? ' clickable' : ''}`}>
            <i className="glyphicon glyphicon-volume-off" />
            {label}
        </div>
    );

    if (!tooltipText || !label) return null;

    return (
        <Tooltip content={tooltipText}>
            {mutingHref ? <a href={mutingHref}>{content}</a> : <span>{content}</span>}
        </Tooltip>
    );
};

MutedChip.propTypes = {
    eventState: PropTypes.string.isRequired,
    detectorId: PropTypes.string.isRequired,
    mutingStates: PropTypes.object,
    hasDetectorMutingRule: PropTypes.bool.isRequired,
};

export default MutedChip;
